import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Business & Data — 8 templates
   revenue_chart, supply_demand, org_hierarchy, calendar_schedule,
   kanban_board, swot_matrix, market_share, growth_curve
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Revenue Chart ────────────────────────────────────── */
export const RevenueChart: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Revenue";
  const values = (props.values as number[]) || [20, 35, 30, 50, 45, 65, 80];
  const fillPercent = (props.fillPercent as number) ?? 100;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      <line x1="100" y1="80" x2="100" y2="460" stroke="#546e7a" strokeWidth="2" />
      <line x1="100" y1="460" x2="740" y2="460" stroke="#546e7a" strokeWidth="2" />
      {/* Area fill */}
      <path d={`M100,460 ${values.map((v, i) => {
        const x = 120 + i * 88;
        const y = 460 - (v / 100) * 360 * (fillPercent / 100);
        return `L${x},${y}`;
      }).join(' ')} L${120 + (values.length - 1) * 88},460 Z`}
        fill="#4caf50" opacity="0.15" />
      {/* Line */}
      <path d={`M${120},${460 - (values[0] / 100) * 360 * (fillPercent / 100)} ${values.map((v, i) => {
        const x = 120 + i * 88;
        const y = 460 - (v / 100) * 360 * (fillPercent / 100);
        return `L${x},${y}`;
      }).join(' ')}`}
        fill="none" stroke="#4caf50" strokeWidth="3" />
      {/* Dots */}
      {values.map((v, i) => {
        const x = 120 + i * 88;
        const y = 460 - (v / 100) * 360 * (fillPercent / 100);
        return <circle key={i} cx={x} cy={y} r="5" fill="#4caf50" />;
      })}
      {/* Dollar sign */}
      <text x="60" y="270" textAnchor="middle" fill="#bdc3c7" fontSize="24" fontWeight="700">$</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Supply & Demand ──────────────────────────────────── */
export const SupplyDemand: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Supply & Demand";
  const activeIndex = (props.activeIndex as number) ?? -1;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Axes */}
      <line x1="120" y1="80" x2="120" y2="460" stroke="#ecf0f1" strokeWidth="2" />
      <line x1="120" y1="460" x2="700" y2="460" stroke="#ecf0f1" strokeWidth="2" />
      <text x="60" y="270" fill="#bdc3c7" fontSize="14" transform="rotate(-90,60,270)">Price</text>
      <text x="400" y="500" textAnchor="middle" fill="#bdc3c7" fontSize="14">Quantity</text>
      {/* Supply curve (upward) */}
      <path d="M160,420 Q350,300 640,120" fill="none"
        stroke={activeIndex === 0 ? "#4caf50" : "#4caf5080"} strokeWidth="3" />
      <text x="650" y="115" fill="#4caf50" fontSize="14" fontWeight="600">S</text>
      {/* Demand curve (downward) */}
      <path d="M160,120 Q350,300 640,420" fill="none"
        stroke={activeIndex === 1 ? "#e74c3c" : "#e74c3c80"} strokeWidth="3" />
      <text x="650" y="425" fill="#e74c3c" fontSize="14" fontWeight="600">D</text>
      {/* Equilibrium point */}
      <circle cx="400" cy="270" r="8" fill="#f9a825" stroke="#fff" strokeWidth="2" />
      <line x1="400" y1="270" x2="400" y2="460" stroke="#f9a825" strokeWidth="1" strokeDasharray="6,4" />
      <line x1="120" y1="270" x2="400" y2="270" stroke="#f9a825" strokeWidth="1" strokeDasharray="6,4" />
      <text x="410" y="260" fill="#f9a825" fontSize="12" fontWeight="600">Equilibrium</text>
      <text x="400" y="550" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Org Hierarchy ────────────────────────────────────── */
export const OrgHierarchy: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Organization";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const roles = (props.roles as string[]) || ["CEO", "VP Sales", "VP Eng", "Mgr A", "Mgr B", "Mgr C", "Mgr D"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Level 0 */}
      <rect x="340" y="60" width="120" height="45" rx="8"
        fill={activeIndex === 0 ? "#3498db" : "#1c2833"} stroke="#3498db" strokeWidth="2" />
      <text x="400" y="88" textAnchor="middle" fill="#ecf0f1" fontSize="13">{roles[0]}</text>
      {/* Lines to level 1 */}
      <line x1="400" y1="105" x2="250" y2="170" stroke="#546e7a" strokeWidth="1.5" />
      <line x1="400" y1="105" x2="550" y2="170" stroke="#546e7a" strokeWidth="1.5" />
      {/* Level 1 */}
      {[{ x: 190, r: roles[1] }, { x: 490, r: roles[2] }].map((n, i) => (
        <g key={i}>
          <rect x={n.x} y="170" width="120" height="45" rx="8"
            fill={activeIndex === i + 1 ? "#2ecc71" : "#1c2833"} stroke="#2ecc71" strokeWidth="2" />
          <text x={n.x + 60} y="198" textAnchor="middle" fill="#ecf0f1" fontSize="12">{n.r}</text>
        </g>
      ))}
      {/* Lines to level 2 */}
      {[[250, 140], [250, 310], [550, 440], [550, 620]].map(([fromX, toX], i) => (
        <line key={i} x1={fromX} y1="215" x2={toX} y2="300" stroke="#546e7a" strokeWidth="1.5" />
      ))}
      {/* Level 2 */}
      {[{ x: 80, r: roles[3] }, { x: 250, r: roles[4] }, { x: 420, r: roles[5] }, { x: 590, r: roles[6] }].map((n, i) => (
        <g key={i}>
          <rect x={n.x} y="300" width="120" height="40" rx="8"
            fill={activeIndex === i + 3 ? "#f39c12" : "#1c2833"} stroke="#f39c12" strokeWidth="1.5" />
          <text x={n.x + 60} y="325" textAnchor="middle" fill="#ecf0f1" fontSize="11">{n.r}</text>
        </g>
      ))}
      <text x="400" y="440" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Calendar Schedule ────────────────────────────────── */
export const CalendarSchedule: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Schedule";
  const activeIndex = (props.activeIndex as number) ?? -1;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Calendar header */}
      <rect x="120" y="60" width="560" height="50" rx="10" fill="#e74c3c" />
      <text x="400" y="92" textAnchor="middle" fill="#fff" fontSize="20" fontWeight="700">Schedule</text>
      {/* Day headers */}
      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
        <text key={i} x={155 + i * 80} y="140" textAnchor="middle" fill="#bdc3c7" fontSize="13" fontWeight="600">{d}</text>
      ))}
      {/* Day cells (3 weeks) */}
      {Array.from({ length: 21 }, (_, i) => {
        const col = i % 7, row = Math.floor(i / 7);
        const x = 120 + col * 80, y = 150 + row * 90;
        return (
          <g key={i}>
            <rect x={x} y={y} width="76" height="85" rx="4"
              fill={i === activeIndex ? "#3498db" : "#1c2833"}
              stroke={i === activeIndex ? "#fff" : "#2c3e50"} strokeWidth="1" />
            <text x={x + 10} y={y + 20} fill="#ecf0f1" fontSize="12">{i + 1}</text>
          </g>
        );
      })}
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Kanban Board ─────────────────────────────────────── */
export const KanbanBoard: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Kanban";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const columns = (props.columns as string[]) || ["To Do", "In Progress", "Done"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Columns */}
      {[
        { x: 60, color: "#e74c3c", cards: 4 },
        { x: 300, color: "#f39c12", cards: 2 },
        { x: 540, color: "#2ecc71", cards: 3 },
      ].map((col, ci) => (
        <g key={ci}>
          {/* Column header */}
          <rect x={col.x} y="60" width="200" height="40" rx="8" fill={col.color} opacity="0.8" />
          <text x={col.x + 100} y="86" textAnchor="middle" fill="#fff" fontSize="15" fontWeight="600">
            {columns[ci] || `Column ${ci + 1}`}
          </text>
          {/* Cards */}
          {Array.from({ length: col.cards }, (_, i) => {
            const cardIdx = ci * 4 + i;
            return (
              <rect key={i} x={col.x + 10} y={120 + i * 80} width="180" height="65" rx="8"
                fill={cardIdx === activeIndex ? col.color : "#1c2833"}
                stroke={cardIdx === activeIndex ? "#fff" : "#2c3e50"} strokeWidth="1.5"
                opacity={cardIdx === activeIndex ? 1 : 0.7} />
            );
          })}
        </g>
      ))}
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. SWOT Matrix ──────────────────────────────────────── */
export const SwotMatrix: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "SWOT Analysis";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const items = (props.items as string[]) || ["Strengths", "Weaknesses", "Opportunities", "Threats"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {[
        { x: 120, y: 70, c: "#4caf50", lbl: items[0] },
        { x: 420, y: 70, c: "#e74c3c", lbl: items[1] },
        { x: 120, y: 300, c: "#3498db", lbl: items[2] },
        { x: 420, y: 300, c: "#f39c12", lbl: items[3] },
      ].map((q, i) => (
        <g key={i}>
          <rect x={q.x} y={q.y} width="260" height="210" rx="12"
            fill={i === activeIndex ? q.c : "#1c2833"}
            stroke={q.c} strokeWidth="2"
            opacity={i === activeIndex ? 0.9 : 0.5} />
          <text x={q.x + 130} y={q.y + 35} textAnchor="middle" fill="#ecf0f1" fontSize="18" fontWeight="700">{q.lbl}</text>
          {/* Bullet points placeholder */}
          {[0, 1, 2].map((j) => (
            <g key={j}>
              <circle cx={q.x + 30} cy={q.y + 65 + j * 35} r="4" fill={q.c} opacity="0.6" />
              <rect x={q.x + 45} y={q.y + 58 + j * 35} width={120 + j * 10} height="10" rx="3" fill="#2c3e50" />
            </g>
          ))}
        </g>
      ))}
      <text x="400" y="555" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Market Share ─────────────────────────────────────── */
export const MarketShare: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Market Share";
  const segments = (props.segments as number[]) || [35, 25, 20, 12, 8];
  const segLabels = (props.segLabels as string[]) || ["A", "B", "C", "D", "E"];
  const colors = ["#3498db", "#e74c3c", "#2ecc71", "#f39c12", "#9b59b6"];
  const activeIndex = (props.activeIndex as number) ?? -1;
  let cumAngle = -90;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {segments.map((seg, i) => {
        const startA = cumAngle;
        const sweep = (seg / 100) * 360;
        cumAngle += sweep;
        const sR = startA * Math.PI / 180, eR = (startA + sweep) * Math.PI / 180;
        const large = sweep > 180 ? 1 : 0;
        const midR = (startA + sweep / 2) * Math.PI / 180;
        const explode = i === activeIndex ? 8 : 0;
        const dx = Math.cos(midR) * explode, dy = Math.sin(midR) * explode;
        return (
          <g key={i}>
            <path d={`M${360 + dx},${270 + dy} L${360 + Math.cos(sR) * 160 + dx},${270 + Math.sin(sR) * 160 + dy}
              A160,160 0 ${large} 1 ${360 + Math.cos(eR) * 160 + dx},${270 + Math.sin(eR) * 160 + dy} Z`}
              fill={colors[i % colors.length]} opacity={i === activeIndex ? 1 : 0.7}
              stroke="#0d1117" strokeWidth="2" />
            {/* Label */}
            <text x={360 + Math.cos(midR) * 200} y={270 + Math.sin(midR) * 200 + 5}
              textAnchor="middle" fill="#ecf0f1" fontSize="12" fontWeight="500">
              {segLabels[i]} ({seg}%)
            </text>
          </g>
        );
      })}
      <text x="360" y="270" textAnchor="middle" fill="#ecf0f1" fontSize="14" fontWeight="600">Total</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Growth Curve ─────────────────────────────────────── */
export const GrowthCurve: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Growth Curve";
  const fillPercent = (props.fillPercent as number) ?? 100;
  const accentColor = (props.accentColor as string) || "#4caf50";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      <line x1="100" y1="80" x2="100" y2="460" stroke="#546e7a" strokeWidth="2" />
      <line x1="100" y1="460" x2="720" y2="460" stroke="#546e7a" strokeWidth="2" />
      {/* S-curve */}
      <path d={`M100,440 Q200,440 300,${440 - (fillPercent / 100) * 40}
        Q400,${440 - (fillPercent / 100) * 200} 500,${440 - (fillPercent / 100) * 320}
        Q600,${440 - (fillPercent / 100) * 350} 700,${440 - (fillPercent / 100) * 355}`}
        fill="none" stroke={accentColor} strokeWidth="3" />
      {/* Area under curve */}
      <path d={`M100,440 Q200,440 300,${440 - (fillPercent / 100) * 40}
        Q400,${440 - (fillPercent / 100) * 200} 500,${440 - (fillPercent / 100) * 320}
        Q600,${440 - (fillPercent / 100) * 350} 700,${440 - (fillPercent / 100) * 355}
        L700,460 L100,460 Z`}
        fill={accentColor} opacity="0.1" />
      {/* Phase labels */}
      <text x="200" y="490" textAnchor="middle" fill="#bdc3c7" fontSize="11">Introduction</text>
      <text x="400" y="490" textAnchor="middle" fill="#bdc3c7" fontSize="11">Growth</text>
      <text x="600" y="490" textAnchor="middle" fill="#bdc3c7" fontSize="11">Maturity</text>
      <text x="400" y="550" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
